/**
 * /admin/announcements — admin-only one-page launcher for every
 * `whats_new` campaign defined in `backend/routers/whats_new.py`.
 *
 * Lists each campaign with: key, audience, title, body preview, link,
 * and "would_send_to" — the live count of users who'd receive it on
 * the next send (audience minus anyone already-sent). Each row has:
 *   • Dry-run  — re-fetch the would_send_to count
 *   • Send     — fire the campaign (confirmation modal); shows the
 *                resulting sent / emailed / errors counters
 *
 * Why this exists: ops sends 2-3 product announcements per month.
 * Memorising the curl syntax + bearer token dance is friction; this
 * page makes it a 3-click action and surfaces "already-sent" counts
 * so post-send sanity-checking is trivial.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { MegaphoneIcon as Megaphone, EnvelopeIcon as Envelope, ArrowClockwiseIcon as ArrowClockwise, PaperPlaneTiltIcon as PaperPlaneTilt, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";

export default function AdminAnnouncements() {
  const [rows, setRows] = useState(null);
  const [busyKey, setBusyKey] = useState("");
  const [confirmCamp, setConfirmCamp] = useState(null);
  const [lastResults, setLastResults] = useState({});

  const reload = async () => {
    try {
      const r = await api.get("/admin/whats-new");
      setRows(r.data?.campaigns || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setRows([]);
    }
  };

  useEffect(() => { reload(); }, []);

  const dryRun = async (key) => {
    setBusyKey(key);
    try {
      const r = await api.post(`/admin/whats-new/dry-run/${key}`);
      toast.success(`Audience: ${r.data?.would_send_to ?? "?"} user(s).`);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey("");
    }
  };

  const send = async (key) => {
    setConfirmCamp(null);
    setBusyKey(key);
    try {
      const r = await api.post(`/admin/whats-new/send/${key}`);
      setLastResults((p) => ({ ...p, [key]: r.data }));
      toast.success(`Sent ${r.data?.sent || 0} (emailed ${r.data?.emailed || 0}, errors ${r.data?.errors || 0}).`);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey("");
    }
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-12" data-testid="admin-announcements-page">
        <div className="flex items-center gap-3 mb-2">
          <Megaphone size={26} weight="duotone" className="text-crimson" />
          <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight">Announcements</h1>
        </div>
        <p className="text-[13.5px] text-[#6B6B6B] leading-snug mb-8 max-w-2xl">
          One-click launcher for every <code className="text-[12px] bg-[#FBF7EC] px-1 py-0.5 rounded">whats_new</code>{" "}
          campaign. Each campaign delivers a branded inbox message to the matched audience and (when{" "}
          <strong>Email</strong> is on) a Resend transactional email. Idempotent — re-running a campaign
          only hits users added since the last send.
        </p>

        {/* Free-form Broadcast Composer — write anything, pick an
            audience, delivered as inbox + push (+ optional email). */}
        <BroadcastComposer />

        <h2 className="font-spectral text-xl tracking-tight mt-12 mb-4">Pre-built campaigns</h2>

        {rows === null && (
          <div className="text-[14px] text-[#6B6B6B]">Loading campaigns…</div>
        )}
        {rows && rows.length === 0 && (
          <div className="rounded-xl border border-[#EAEAEA] bg-white p-8 text-center">
            <p className="text-[14px] text-[#6B6B6B]">
              No campaigns defined yet. Add one in{" "}
              <code className="text-[12px] bg-[#FBF7EC] px-1 py-0.5 rounded">backend/routers/whats_new.py</code>.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {(rows || []).map((c) => {
            const last = lastResults[c.key];
            const sendable = (c.would_send_to ?? 0) > 0;
            return (
              <div
                key={c.key}
                className={`rounded-xl border bg-white p-5 sm:p-6 ${
                  sendable ? "border-crimson/40 shadow-[0_4px_18px_rgba(193,39,45,0.10)]" : "border-[#EAEAEA]"
                }`}
                data-testid={`campaign-row-${c.key}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h2 className="font-spectral text-xl tracking-tight">{c.title}</h2>
                      {c.send_email && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FBF7EC] border border-[#E8D9A6] text-[10px] font-bold uppercase tracking-[0.12em] text-[#8A6A00]">
                          <Envelope size={10} weight="fill" /> Email
                        </span>
                      )}
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-ink/5 border border-[#EAEAEA] text-[10px] font-bold uppercase tracking-[0.12em] text-[#3F3F3F]">
                        {c.audience}
                      </span>
                    </div>
                    <p className="text-[13px] text-[#6B6B6B] leading-snug mb-2 line-clamp-3">
                      {c.body_preview}
                    </p>
                    {c.link && (
                      <p className="text-[11px] text-[#6B6B6B]">
                        Deep-links to{" "}
                        <Link to={c.link} className="underline font-semibold text-ink hover:text-crimson">
                          {c.link}
                        </Link>
                      </p>
                    )}
                    <p className="text-[11px] text-[#9A9A9A] mt-1 font-mono">{c.key}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2 sm:min-w-[180px] flex-shrink-0">
                    <div
                      className={`text-right ${
                        sendable ? "text-crimson" : "text-[#6B6B6B]"
                      }`}
                      data-testid={`campaign-audience-${c.key}`}
                    >
                      <div className="text-[28px] font-light leading-none font-tabular">
                        {c.would_send_to >= 0 ? c.would_send_to.toLocaleString() : "—"}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.15em] font-bold mt-0.5">
                        Would send to
                      </div>
                    </div>
                    <div className="flex gap-2 mt-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={busyKey === c.key}
                        onClick={() => dryRun(c.key)}
                        data-testid={`campaign-dry-run-${c.key}`}
                        className="h-8 px-3 text-[11.5px]"
                      >
                        <ArrowClockwise size={12} weight="bold" className="mr-1" /> Re-check
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        disabled={busyKey === c.key || !sendable}
                        onClick={() => setConfirmCamp(c)}
                        data-testid={`campaign-send-${c.key}`}
                        className="h-8 px-3 text-[11.5px] bg-ink hover:bg-crimson text-white disabled:opacity-40"
                      >
                        <PaperPlaneTilt size={12} weight="bold" className="mr-1" /> Send
                      </Button>
                    </div>
                  </div>
                </div>
                {last && (
                  <div
                    className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-900 flex items-center gap-2"
                    data-testid={`campaign-result-${c.key}`}
                  >
                    <CheckCircle size={14} weight="fill" />
                    <span>
                      Last fire: sent <strong>{last.sent || 0}</strong>, emailed <strong>{last.emailed || 0}</strong>,
                      errors <strong>{last.errors || 0}</strong> (audience {last.audience_size || 0}).
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!confirmCamp} onOpenChange={(o) => !o && setConfirmCamp(null)}>
        <DialogContent data-testid="campaign-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Send this campaign?</DialogTitle>
            <DialogDescription>
              About to send <strong>{confirmCamp?.title}</strong> to{" "}
              <strong>{confirmCamp?.would_send_to?.toLocaleString?.() || 0}</strong> user
              {confirmCamp?.would_send_to === 1 ? "" : "s"}.
              {confirmCamp?.send_email
                ? " Each user will get a branded email AND an inbox message."
                : " Each user will get an inbox message only (no email)."}{" "}
              This is idempotent — anyone already messaged on a prior fire will be skipped automatically.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCamp(null)} data-testid="campaign-confirm-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => confirmCamp && send(confirmCamp.key)}
              className="bg-crimson hover:bg-ink text-white"
              data-testid="campaign-confirm-fire"
            >
              <PaperPlaneTilt size={13} weight="bold" className="mr-1" /> Yes, send now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function BroadcastComposer() {
  const [audiences, setAudiences] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", link: "", audience: "everyone", send_email: false });
  const [busy, setBusy] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [history, setHistory] = useState([]);

  const loadMeta = async () => {
    try {
      const [a, h] = await Promise.all([api.get("/admin/broadcasts/audiences"), api.get("/admin/broadcasts/history")]);
      setAudiences(a.data?.audiences || []);
      setHistory(h.data?.broadcasts || []);
    } catch { /* non-blocking */ }
  };
  useEffect(() => { loadMeta(); }, []);

  const selected = audiences.find((a) => a.key === form.audience);
  const valid = form.title.trim().length >= 3 && form.body.trim().length >= 10;

  const sendTest = async () => {
    setBusy("test");
    try {
      await api.post("/admin/broadcasts/test", { ...form, link: form.link.trim() || null });
      toast.success("Test sent to you — check your inbox (and email if toggled).");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(""); }
  };

  const send = async () => {
    setBusy("send");
    try {
      const r = await api.post("/admin/broadcasts/send", { ...form, link: form.link.trim() || null });
      toast.success(`Broadcast sent to ${r.data?.sent || 0} members (${r.data?.emailed || 0} emailed).`);
      setConfirming(false);
      setForm({ title: "", body: "", link: "", audience: "everyone", send_email: false });
      loadMeta();
    } catch (e) {
      toast.error(formatApiError(e));
      setConfirming(false);
    } finally { setBusy(""); }
  };

  return (
    <div className="rounded-2xl border-2 border-gold/40 bg-white p-6" data-testid="broadcast-composer">
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#8A6A00] mb-1.5 flex items-center gap-1.5">
        <PaperPlaneTilt size={13} weight="duotone" /> Compose a broadcast
      </p>
      <p className="text-[12.5px] text-[#6B6B6B] mb-4">
        Delivered as an inbox message + phone push to every matched member. Email is optional.
        "Everyone" blasts require a test-send to yourself first.
      </p>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <input
          value={form.title} maxLength={90}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Title — e.g. Zoomie Time is live!"
          className="h-10 rounded-md border border-input bg-white px-3 text-sm w-full"
          data-testid="broadcast-title-input"
        />
        <select
          value={form.audience}
          onChange={(e) => setForm({ ...form, audience: e.target.value })}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm w-full"
          data-testid="broadcast-audience-select"
        >
          {audiences.map((a) => (
            <option key={a.key} value={a.key}>{a.label}{a.count >= 0 ? ` (${a.count})` : ""}</option>
          ))}
        </select>
      </div>
      <textarea
        value={form.body} rows={4} maxLength={2000}
        onChange={(e) => setForm({ ...form, body: e.target.value })}
        placeholder="Your message… line breaks become paragraphs in the email."
        className="rounded-md border border-input bg-white px-3 py-2 text-sm w-full mb-3"
        data-testid="broadcast-body-input"
      />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          value={form.link}
          onChange={(e) => setForm({ ...form, link: e.target.value })}
          placeholder="Optional link — e.g. /find-pros?kind=field_host"
          className="h-9 rounded-md border border-input bg-white px-3 text-[13px] flex-1 min-w-[220px]"
          data-testid="broadcast-link-input"
        />
        <label className="flex items-center gap-2 text-[13px] cursor-pointer">
          <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
            className="h-4 w-4 accent-[#8A6A00]" data-testid="broadcast-email-toggle" />
          <Envelope size={14} weight="duotone" /> Also send email
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" className="rounded-full" disabled={!valid || busy !== ""}
          onClick={sendTest} data-testid="broadcast-test-btn">
          {busy === "test" ? "Sending…" : "Send test to me"}
        </Button>
        <Button size="sm" className="rounded-full" disabled={!valid || busy !== ""}
          onClick={() => setConfirming(true)} data-testid="broadcast-send-btn">
          Send to {selected?.label || form.audience}{selected?.count >= 0 ? ` (${selected.count})` : ""}
        </Button>
      </div>
      {history.length > 0 && (
        <div className="mt-5 pt-4 border-t border-[#EAEAEA]" data-testid="broadcast-history">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#6B6B6B] mb-2">Recent broadcasts</p>
          {history.slice(0, 4).map((b) => (
            <p key={b.id} className="text-[12px] text-[#6B6B6B]">
              <strong className="text-ink">{b.title}</strong> → {b.audience} · sent {b.sent}, emailed {b.emailed} · {(b.created_at || "").slice(0, 10)}
            </p>
          ))}
        </div>
      )}
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent data-testid="broadcast-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Send this broadcast?</DialogTitle>
            <DialogDescription>
              "{form.title}" will go to <strong>{selected?.label || form.audience}</strong>
              {selected?.count >= 0 ? ` — ${selected.count} members` : ""} as an inbox message + push
              {form.send_email ? " AND a branded email" : " (no email)"}. This cannot be unsent.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)} data-testid="broadcast-cancel-btn">Cancel</Button>
            <Button onClick={send} disabled={busy === "send"} data-testid="broadcast-confirm-btn">
              {busy === "send" ? "Sending…" : "Yes, send it"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
